// combatpro/monster/monsterPersistence.js
//
// Combat PRO - Monster Studio persistence API (Stage 58: Persistence & Cloud CRUD;
// Stage 63: Encounter Template records).
//
// The backend-communication layer for custom monsters. It owns nothing but the
// HTTP calls to the existing Combat PRO user-monster endpoints and the mapping of
// raw records into studio models (delegated to the Stage 55 engine). It is the
// direct analogue of the Stage 51 spell persistence module, and uses the same
// shared portal axios client (src/api.js: baseURL https://api.visinvoke.com,
// withCredentials cookie auth, no Authorization header).
//
// Existing endpoints reused verbatim (battletracker.html: fetchUserMonsters 3981,
// viStudioCloudSave 10956, viStudioCloudDelete 10969) - no new APIs, no contract
// changes:
//   GET    /v1/battletracker/user/monsters            -> [{ data, ... }, ...]
//   POST   /v1/battletracker/user/monsters            -> upsert by id
//          body { id, name, displayName, data }
//   DELETE /v1/battletracker/user/monsters/{id}        -> remove by id
//
// The GET response is an array of stored records; each record's studio model lives
// on record.data (legacy fetchUserMonsters maps m => m.data). The POST body mirrors
// viStudioCloudSave exactly: id and name both carry the customMonsterId, displayName
// carries the friendly name, and data carries the full studio model.
//
// Stage 63 (Encounter Templates): the legacy template subsystem stored templates in
// localStorage / on the encounter session cloud save. That mechanism is not migrated;
// instead template records share this same /v1/battletracker/user/monsters store,
// distinguished by data.recordType === ENCOUNTER_TEMPLATE. No new endpoint, no second
// persistence system, no duplicate CRUD: template save reuses the same POST, template
// delete reuses the same DELETE (deleteUserTemplate delegates to deleteUserMonster),
// and template load reuses the same GET (fetchUserMonsters) with a discriminator
// filter. The only behavioural change to existing monster loading is that
// loadUserMonsters now skips template records so they never appear as monsters in My
// Library.
//
// No React, no DOM, no localStorage. No optional chaining. No em dash.

import API from '../../../../api';
import {
  normalizeStudioMonster,
  nextMonsterId
} from '../../../monster/monsterStudioEngine';
import { ENCOUNTER_TEMPLATE } from '../../../monster/monsterTemplateEngine';

var USER_MONSTERS_PATH = '/v1/battletracker/user/monsters';

// Raw fetch: returns the array of stored payloads (record.data), unnormalized. This
// returns BOTH custom-monster and encounter-template payloads; callers filter by
// recordType (loadUserMonsters excludes templates, loadUserTemplates selects them).
export async function fetchUserMonsters() {
  try {
    var res = await API.get(USER_MONSTERS_PATH);
    var data = res ? res.data : null;
    if (!Array.isArray(data)) { return []; }
    return data
      .map(function(rec) { return (rec && rec.data) ? rec.data : rec; })
      .filter(function(d) { return d && typeof d === 'object'; });
  } catch (e) {
    return [];
  }
}

// Load + normalize into studio models (the "load custom pool" operation). Records
// missing an id receive a pool-aware MON-###### fallback, mirroring how the legacy
// viStudioLoadLibrary normalized through viStudioNormalize / viNextMonId. Stage 63:
// encounter-template records are skipped so they never surface as monsters.
export async function loadUserMonsters() {
  var raw = await fetchUserMonsters();
  var out = [];
  for (var i = 0; i < raw.length; i++) {
    if (raw[i] && raw[i].recordType === ENCOUNTER_TEMPLATE) { continue; }
    var fallback = nextMonsterId(out, 0).id;
    out.push(normalizeStudioMonster(raw[i], fallback));
  }
  return out;
}

// Upsert a single monster. Body shape matches viStudioCloudSave. Resolves true on a
// 2xx response (axios rejects non-2xx), false on any failure.
export async function saveUserMonster(monster) {
  try {
    var m = monster || {};
    var body = {
      id: m.customMonsterId,
      name: m.customMonsterId,
      displayName: m.customName || m.name || 'Custom Monster',
      data: m
    };
    await API.post(USER_MONSTERS_PATH, body);
    return true;
  } catch (e) {
    return false;
  }
}

// Delete by id. Resolves true on success, false on failure.
export async function deleteUserMonster(id) {
  try {
    await API.delete(USER_MONSTERS_PATH + '/' + encodeURIComponent(id));
    return true;
  } catch (e) {
    return false;
  }
}

// ---- Stage 63: Encounter Template records ----------------------------------

// Load the encounter-template records from the shared store. Reuses the same GET as
// the monster pool (fetchUserMonsters) and keeps only template records by
// discriminator. Returns the template payloads ({ recordType, id, name, entries, ts }).
export async function loadUserTemplates() {
  var raw = await fetchUserMonsters();
  var out = [];
  for (var i = 0; i < raw.length; i++) {
    if (raw[i] && raw[i].recordType === ENCOUNTER_TEMPLATE) { out.push(raw[i]); }
  }
  return out;
}

// Upsert a single encounter-template record. Reuses the same POST body shape as
// saveUserMonster: id and name carry the template id, displayName carries the
// friendly name, and data carries the full template record (with its recordType).
export async function saveUserTemplate(template) {
  try {
    var t = template || {};
    var body = {
      id: t.id,
      name: t.id,
      displayName: t.name || 'Encounter Template',
      data: t
    };
    await API.post(USER_MONSTERS_PATH, body);
    return true;
  } catch (e) {
    return false;
  }
}

// Delete a template by id. Reuses the existing DELETE operation verbatim (no second
// delete path); kept as a named export for call-site clarity.
export function deleteUserTemplate(id) {
  return deleteUserMonster(id);
}
